import { bucketContaining, sortedForTaskList, type BucketableTask, type IslandColor } from './agent-task-buckets';

// The shared view-model for the island quick-view (the pill-anchored bubble on every surface). Pure and
// mirrored 1:1 on native so all three show the same rows in the same order.
//
// **The quick-view is a PROJECTION of the account-wide Tasks list, not a second list.** Same rows (the
// caller hands it the same set the Tasks screen renders, outreach rows included), same recency window,
// and — the part this used to get wrong — the SAME reading order: unfinished work above finished work,
// newest-updated within each half (`sortedForTaskList`). Sorting on `updatedDate` alone is how the bubble
// came to show six rows, four of them Done, under a header reading "2 tasks in progress": finishing is a
// change, so a task that completed a minute ago outranked one that had been running for hours. The rule
// lives in `agent-task-buckets` because every surface that lists tasks has to apply the same one.
//
// Capped to a peek with a "+N more" count and a "see all" footer. Each row carries enough to read at a
// glance — status, title, the Space it lives in, its agent, and a short summary. The row mapping happens
// at the call site.

export interface QuickViewTask extends BucketableTask {
  id: string;
  title: string;
  /** Required here (widened from BucketableTask's optional) so the row's StatusBadge always has a value. */
  status: string;
  agentId?: string;
  workspaceId: string;
  workspaceName: string;
  /** A short "what it's about" line, when the task carries one. */
  summary?: string;
  /**
   * This row is an inbound OUTREACH, not an AgentTask — a message from another person's agent, which the
   * account-wide list files beside the tasks (see `/api/tasks`'s `outreachToWebTask`). It bucket, badges
   * and sorts like any task; the flag only tells the row how to title itself and what a click opens,
   * exactly as `TaskCard` branches on `rowKind`. Mirrors iOS/Android `SymphonyTask.isOutreach`.
   */
  isOutreach?: boolean;
  /** Who sent it — an outreach row is titled by the person, since its subject is a person not a task. */
  senderName?: string;
}

export interface IslandQuickView {
  /** The peek — the top of the Tasks list in reading order, capped to `QUICK_VIEW_SHOWN`. */
  rows: QuickViewTask[];
  /** Tasks in the window beyond the shown rows → the footer's "+N". */
  moreCount: number;
  /**
   * The FULL windowed task list in reading order — the source for the pill's lead task (line 2 of the
   * expanded blue/orange capsule). Kept alongside the capped `rows` because the newest task in a bucket
   * can fall outside the six-row peek, so the lead must be picked from the whole set.
   */
  sorted: QuickViewTask[];
}

export const QUICK_VIEW_SHOWN = 6;

/**
 * The pill's line-2 lead: the single most-recent task in the color's bucket, plus how many more sit
 * behind it. Blue reads the `inProgress` bucket (the current action beat); orange reads `waiting`
 * (the ask) — `failed` is not in it, matching the count the pill shows. The status line is the task's
 * `summary` when it carries one, else its title. Pure and mirror-able 1:1 on native — it reads the same
 * windowed list the quick-view rows come from.
 */
export interface IslandLead {
  taskId: string;
  agentId?: string;
  /** The current action beat (blue) / the ask (orange): the lead task's summary, else its title. */
  statusLine: string;
  /** Tasks in the same bucket beyond the lead → the pill's line-2 "+N". */
  extraCount: number;
}

export function islandLead(sorted: QuickViewTask[], color: IslandColor): IslandLead | null {
  if (color !== 'blue' && color !== 'orange') return null;
  const inBucket = sorted.filter((task) => {
    const bucket = bucketContaining(task.status);
    // Orange counts the waiting bucket alone (see islandCountsFromRows), so its lead task comes from
    // there too. Drawing one from `failed` would headline a task the pill is no longer counting.
    return color === 'blue' ? bucket === 'inProgress' : bucket === 'waiting';
  });
  const lead = inBucket[0];
  if (!lead) return null;
  return {
    taskId: lead.id,
    agentId: lead.agentId,
    statusLine: lead.summary || lead.title,
    extraCount: inBucket.length - 1,
  };
}

export function buildIslandQuickView(tasks: QuickViewTask[], now: number = Date.now()): IslandQuickView {
  // The Tasks list's own order — NOT a local recency sort. See the header note.
  const sorted = sortedForTaskList(tasks, now);
  return {
    rows: sorted.slice(0, QUICK_VIEW_SHOWN),
    moreCount: Math.max(0, sorted.length - QUICK_VIEW_SHOWN),
    sorted,
  };
}
